import { useEffect, useState } from "react";
import { Download } from "lucide-react";

/**
 * "Install app" button — appears only when the browser fires the
 * `beforeinstallprompt` event (Chrome/Edge desktop + Android) and the app
 * isn't already installed. Self-hides everywhere else (incl. iOS Safari,
 * which has no install API). Pass a className to fit the surrounding theme.
 */
export default function InstallPwaButton({ className = "", label = "Install app" }) {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setHidden(true);
      return;
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setHidden(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (hidden || !deferred) return null;

  const onClick = async () => {
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* user dismissed */
    }
    setDeferred(null);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="install-pwa-btn"
      className={
        className ||
        "inline-flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100"
      }
    >
      <Download className="h-4 w-4" /> {label}
    </button>
  );
}
